import { AxiosResponse } from 'axios';
import { IGetSimpleListFilters, IGetSimpleListResponse } from 'models/http/common';

import {
  CreateFieldTempleteDataUploadRequest,
  CreateFieldTempleteDataUploadResponse,
  DeleteFieldTempleteDataUploadResponse,
  GetFieldTempleteDataUploadResponse,
  IDataUploadCreateDraftRequest,
  IDataUploadCreateDraftResponse,
  IDataUploadGetSchemaResponse,
  IDataUploadSubmitData,
  IDataUploadSubmitResponse,
  IGetDataUploadJobAuditResponse,
  IGetDataUploadJobDetailsResponse,
  IGetDataUploadJobsFilters,
  IGetDataUploadJobsResponse,
  UpdateFieldTempleteDataUploadRequest,
  UpdateFieldTempleteDataUploadResponse,
} from 'models/http/data-upload';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { DataUploadModels } from 'utils/enums/model-names';
import { serverAxios } from 'utils/http';

abstract class DataUploadBaseHttp {
  private readonly dataUploadRoute = 'data-upload';
  protected abstract readonly route: DataUploadModels;

  protected get wireRoute(): string {
    // Converts underscores to hyphens for the URL. The data upload DTO uses a
    // transformer to reverse this conversion on the backend.
    return this.route.replace(/_/g, '-');
  }

  public async dataUploadCreateDraft(
    data: IDataUploadCreateDraftRequest,
    abortController?: AbortController,
  ): Promise<IDataUploadCreateDraftResponse> {
    return serverAxios
      .post(`/${this.wireRoute}/${this.dataUploadRoute}/draft`, data, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IDataUploadCreateDraftResponse>) => {
        return response.data;
      });
  }

  public async dataUploadSubmit(data: IDataUploadSubmitData, abortController?: AbortController): Promise<number> {
    return serverAxios
      .post(`/${this.wireRoute}/${this.dataUploadRoute}/submit`, data, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IDataUploadSubmitResponse>) => {
        return response.data.id;
      });
  }

  public async dataUploadIngestionProcess(id: number, abortController?: AbortController): Promise<void> {
    await serverAxios.post(
      `/${this.wireRoute}/${this.dataUploadRoute}/ingestion/${id}/process`,
      {},
      {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      },
    );
  }

  public async dataUploadIngestionMerge(id: number, abortController?: AbortController): Promise<void> {
    await serverAxios.post(
      `/${this.wireRoute}/${this.dataUploadRoute}/ingestion/${id}/merge`,
      {},
      {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      },
    );
  }

  public async dataUploadGetFieldsMapping(abortController?: AbortController): Promise<IDataUploadGetSchemaResponse> {
    return serverAxios
      .get(`/${this.wireRoute}/${this.dataUploadRoute}/schema`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IDataUploadGetSchemaResponse>) => {
        return response.data;
      });
  }

  public async getFieldTemplatesSimple(_?: IGetSimpleListFilters, abortController?: AbortController): Promise<IGetSimpleListResponse> {
    return serverAxios
      .get(`/${this.wireRoute}/${this.dataUploadRoute}/templates/simple`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async getFieldTemplateById(id: number, abortController?: AbortController): Promise<GetFieldTempleteDataUploadResponse> {
    return serverAxios
      .get(`/${this.wireRoute}/${this.dataUploadRoute}/templates/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<GetFieldTempleteDataUploadResponse>) => {
        return response.data;
      });
  }

  public async createFieldTemplate(data: CreateFieldTempleteDataUploadRequest): Promise<CreateFieldTempleteDataUploadResponse> {
    return serverAxios
      .post(`/${this.wireRoute}/${this.dataUploadRoute}/templates`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<CreateFieldTempleteDataUploadResponse>) => {
        return response.data;
      });
  }

  public async updateFieldTemplate(id: number, data: UpdateFieldTempleteDataUploadRequest): Promise<UpdateFieldTempleteDataUploadResponse> {
    return serverAxios
      .patch(`/${this.wireRoute}/${this.dataUploadRoute}/templates/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<UpdateFieldTempleteDataUploadResponse>) => {
        return response.data;
      });
  }

  public async deleteFieldTemplate(id: number): Promise<DeleteFieldTempleteDataUploadResponse> {
    return serverAxios
      .delete(`/${this.wireRoute}/${this.dataUploadRoute}/templates/${id}`, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<DeleteFieldTempleteDataUploadResponse>) => {
        return response.data;
      });
  }

  /**
   * Cross-module endpoint — bypass the per-module `route` prefix and hit
   * `/data-upload/jobs` directly.
   */
  public async dataUploadGetJobs(
    filters: IGetDataUploadJobsFilters,
    abortController?: AbortController,
  ): Promise<IGetDataUploadJobsResponse> {
    return serverAxios
      .get(`/${this.dataUploadRoute}/jobs`, {
        params: { page: filters.page, limit: filters.limit },
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDataUploadJobsResponse>) => {
        return response.data;
      });
  }

  public async dataUploadGetJobDetails(id: number, abortController?: AbortController): Promise<IGetDataUploadJobDetailsResponse> {
    return serverAxios
      .get(`/${this.dataUploadRoute}/jobs/${id}/details`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDataUploadJobDetailsResponse>) => {
        return response.data;
      });
  }

  public async dataUploadGetJobAudit(id: number, abortController?: AbortController): Promise<IGetDataUploadJobAuditResponse> {
    return serverAxios
      .get(`/${this.dataUploadRoute}/jobs/${id}/audit`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDataUploadJobAuditResponse>) => {
        return response.data;
      });
  }
}

export default DataUploadBaseHttp;
